import React, { useState } from "react";
import { Box } from "@mui/material";
import { CustomButton, CustomLoading } from "@ippon/shared-components";
import { useForm } from "react-hook-form";
import OtpInput from "../otp/OtpInput";
import AuthInput from "../login/AuthInput";
import { passwordRules } from "../../utils/validationRules";
import "../login/Login.css";

export type PasswordResetPageProps = {
  renderLogo?: React.ReactNode;
  logoSrc?: string;
  logoAlt?: string;
  logoStyle?: React.CSSProperties;
  email: string;
  onSubmit: (values: {
    otp: string;
    password: string;
    confirmPassword: string;
  }) => Promise<{ ok: boolean; message?: string }>;
  onResend: () => Promise<{ ok: boolean; message?: string }>;
  submitButtonLabel?: string;
};

type ResetFormInputs = {
  password: string;
  confirmPassword: string;
};

const PasswordResetPage: React.FC<PasswordResetPageProps> = ({
  renderLogo,
  logoSrc,
  logoAlt,
  logoStyle,
  email,
  onSubmit,
  onResend,
  submitButtonLabel = "登録する",
}) => {
  const [otpValue, setOtpValue] = useState("");
  const [otpError, setOtpError] = useState("");
  const [isResending, setIsResending] = useState(false);
  const [passwordMismatchError, setPasswordMismatchError] = useState("");
  const [passwordApiError, setPasswordApiError] = useState("");
  const [resendSuccess, setResendSuccess] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
  } = useForm<ResetFormInputs>();

  const passwordValue = watch("password");
  const confirmPasswordValue = watch("confirmPassword");
  const isOtpComplete = otpValue.length === 6;
  const isFormValid = isOtpComplete && !!passwordValue && !!confirmPasswordValue;

  const handleResend = async () => {
    setIsResending(true);
    try {
      const result = await onResend();
      if (result.ok) {
        setOtpError("");
        setResendSuccess(true);
        setTimeout(() => setResendSuccess(false), 3000);
      } else {
        setOtpError(result.message || "再送に失敗しました");
      }
    } finally {
      setIsResending(false);
    }
  };

  const isPasswordError = (message: string) =>
    /パスワード/.test(message) && !/認証コード|ワンタイム/.test(message);

  const doSubmit = async (data: ResetFormInputs) => {
    setOtpError("");
    setPasswordMismatchError("");
    setPasswordApiError("");

    if (data.password !== data.confirmPassword) {
      setPasswordMismatchError("パスワードが一致しません");
      return;
    }

    const result = await onSubmit({
      otp: otpValue,
      password: data.password,
      confirmPassword: data.confirmPassword,
    });

    if (!result.ok) {
      const msg = result.message || "認証コードが正しくありません。";
      if (isPasswordError(msg)) {
        setPasswordApiError(msg);
      } else {
        setOtpError(msg);
      }
    }
  };

  return (
    <Box className="container">
      <Box
        component="form"
        onSubmit={handleSubmit(doSubmit)}
        noValidate
        autoComplete="off"
        className="formContainer"
      >
        <Box className="fieldContainer">
          <Box className="logoContainer">
            {renderLogo ? (
              renderLogo
            ) : logoSrc ? (
              <img src={logoSrc} alt={logoAlt || "Logo"} style={logoStyle} />
            ) : null}
          </Box>

          <Box className="check_mail_content">
            <p className="check_mail_title">パスワード再発行</p>
            <p
              style={{
                fontSize: "12px",
                color: "#3e3e3e",
                margin: 0,
                textAlign: "left",
                lineHeight: 1.6,
              }}
            >
              <span style={{ fontWeight: 600 }}>{email}</span>{" "}
              に認証コードを記載したメールを送信しました。
              <br />
              メール本文の認証コードを入力し、新しいパスワードを設定して「登録する」でお進みください。
            </p>
          </Box>

          <Box sx={{ position: "relative" }}>
            <Box className="inputContainer" style={{ gap: 0 }}>
            <p
              style={{
                fontSize: "12px",
                fontWeight: 500,
                margin: 0,
                marginBottom: 4,
                color: "#3e3e3e",
              }}
            >
              認証コード
            </p>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <Box sx={{ "& > div": { gap: "6px" }, flexShrink: 0}}>
                <OtpInput
                  value={otpValue}
                  onChange={(val) => {
                    setOtpValue(val);
                    if (otpError) setOtpError("");
                  }}
                  length={6}
                  disabled={false}
                  error={!!otpError}
                  inputStyle={{ width: 28, height: 34, fontSize: 14 }}
                />
              </Box>
              <Box sx={{ position: "relative", flexShrink: 0, width: 40 }}>
                <button
                  type="button"
                  onClick={handleResend}
                  disabled={isResending}
                  style={{
                    background: "transparent",
                    border: "none",
                    color: "#0b9dbd",
                    cursor: isResending ? "default" : "pointer",
                    fontSize: 12,
                    fontWeight: 600,
                    whiteSpace: "nowrap",
                    padding: 0,
                    visibility: isResending ? "hidden" : "visible",
                  }}
                >
                  再送信
                </button>
                {isResending && (
                  <Box sx={{ position: "absolute", top: 0, left: 0, right: 0, bottom: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <CustomLoading
                      loading={true}
                      fullScreen={false}
                      size="small"
                      variant="circular"
                    />
                  </Box>
                )}
              </Box>
            </Box>
            </Box>
            {(otpError || resendSuccess) && (
              <p
                style={{
                  color: otpError ? "#d32f2f" : "#4caf50",
                  fontSize: 12,
                  fontWeight: 500,
                  margin: 0,
                  textAlign: "left",
                  position: "absolute",
                  top: "100%",
                  left: 0,
                }}
              >
                {otpError || "送信完了しました"}
              </p>
            )}
          </Box>

          <Box className="inputContainer" sx={{ mt: otpError ? "16px" : 0 }}>
            <AuthInput
              label="パスワード"
              name="password"
              placeholder="パスワードを入力してください"
              type="password"
              register={register}
              error={!!errors.password || !!passwordApiError}
              helperText={
                typeof errors.password?.message === "string"
                  ? errors.password.message
                  : undefined
              }
              apiError={passwordApiError}
              isRequired={true}
              rules={passwordRules}
              showPasswordToggle={true}
            />
            <AuthInput
              label="パスワード確認"
              name="confirmPassword"
              placeholder="パスワードを再入力してください"
              type="password"
              register={register}
              error={!!errors.confirmPassword || !!passwordMismatchError}
              helperText={
                typeof errors.confirmPassword?.message === "string"
                  ? errors.confirmPassword.message
                  : passwordMismatchError || undefined
              }
              isRequired={true}
              rules={passwordRules}
              showPasswordToggle={true}
            />
          </Box>

          <CustomButton
            type="submit"
            label={submitButtonLabel}
            disabled={!isFormValid}
            fullWidth
          />
        </Box>
      </Box>
    </Box>
  );
};

export default PasswordResetPage;
